/**
 * Бэкенд отдаёт момент в UTC, но иногда без суффикса Z / без смещения.
 * Тогда ECMAScript трактует строку как локальное время — на экране другое время.
 * Нормализуем к ISO UTC (…Z), чтобы Date и toLocale* соответствовали UTC с бэка.
 *
 * Также поддерживаются Unix time: строка из 10 цифр (секунды) или 13 (миллисекунды),
 * в т.ч. ошибочно дописанный суффикс Z (например "1775729930Z" после старой логики).
 */
export function normalizeBackendUtcIsoString(input: string): string {
  const s = String(input).trim();
  if (!s) return s;

  const unixWithZ = s.match(/^(\d{10}|\d{13})Z$/i);
  if (unixWithZ) {
    const digits = unixWithZ[1];
    const n = Number(digits);
    const d = digits.length === 10 ? new Date(n * 1000) : new Date(n);
    return Number.isNaN(d.getTime()) ? s : d.toISOString();
  }

  if (/^\d{10}$/.test(s)) {
    const n = Number(s);
    if (n >= 1_000_000_000 && n < 10_000_000_000) {
      const d = new Date(n * 1000);
      return Number.isNaN(d.getTime()) ? s : d.toISOString();
    }
  }

  if (/^\d{13}$/.test(s)) {
    const n = Number(s);
    if (n >= 1_000_000_000_000 && n < 10_000_000_000_000) {
      const d = new Date(n);
      return Number.isNaN(d.getTime()) ? s : d.toISOString();
    }
  }

  if (/([Zz]|[+-]\d{2}:?\d{2})$/.test(s)) return s;
  const withT =
    /\d{4}-\d{2}-\d{2}\s/.test(s) && !s.includes('T')
      ? s.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T')
      : s;
  return `${withT}Z`;
}

function parseChatMessageDate(isoOrBackendString: string): Date | null {
  const normalized = normalizeBackendUtcIsoString(isoOrBackendString);
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Календарный день сообщения в локальной таймзоне (YYYY-MM-DD) */
export function chatMessageLocalDayKey(isoOrBackendString: string): string | null {
  const d = parseChatMessageDate(isoOrBackendString);
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Подпись разделителя дня в чате (Сегодня / Вчера / полная дата) */
export function formatChatDayDividerLabel(isoOrBackendString: string): string {
  const d = parseChatMessageDate(isoOrBackendString);
  if (!d) return '';

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfThat = new Date(d);
  startOfThat.setHours(0, 0, 0, 0);
  const diffDays = Math.round((startOfToday.getTime() - startOfThat.getTime()) / 86400000);

  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';
  if (diffDays === 2) return 'Позавчера';

  const nowY = startOfToday.getFullYear();
  return d.toLocaleDateString('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() !== nowY ? { year: 'numeric' as const } : {}),
  });
}

/** Время сообщения чата в локали и таймзоне браузера пользователя */
export function formatChatMessageTime(isoOrBackendString: string): string {
  const d = parseChatMessageDate(isoOrBackendString);
  if (!d) return isoOrBackendString;
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Форматирует Unix timestamp в читаемую дату
 * @param value - Unix timestamp в секундах или миллисекундах, или строка даты
 * @returns Отформатированная дата в формате DD.MM.YYYY HH:mm
 */
export const formatUnixTimestamp = (value: number | string): string => {
  // Обрабатываем Unix timestamp (секунды)
  const timestamp = typeof value === 'number' ? value * 1000 : (value as string | number);
  const date = new Date(timestamp);

  return date.toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};
